// Utilities for parsing tokens from URL and sanitizing the address bar.

// --- AES-256-CBC Decryption (matches C# EncryptionHelper / Angular EncryptionService) ---
// Key:    PBKDF2(passphrase, salt, 10000 iterations, SHA-1) → 256-bit
// IV:     16 bytes, prepended to ciphertext
// Format: base64url(IV[16] + ciphertext)

const PBKDF2_SALT = process.env.REACT_APP_ENC_SALT || '';
const PBKDF2_ITERATIONS = Number(process.env.REACT_APP_ENC_ITERATIONS) || 10000;
const IV_BYTES = 16;

/**
 * Retrieves the encryption passphrase from environment variable.
 */
function getEncryptionKey() {
  return process.env.REACT_APP_ENC_KEY || '';
}

/**
 * Derives a 256-bit AES-CBC key from a passphrase using PBKDF2 (SHA-1).
 */
async function deriveKey(passphrase) {
  const enc = new TextEncoder();
  const baseKey = await crypto.subtle.importKey(
    'raw',
    enc.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: enc.encode(PBKDF2_SALT),
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-1',
    },
    baseKey,
    { name: 'AES-CBC', length: 256 },
    false,
    ['decrypt']
  );
}

/**
 * Decodes a base64url-encoded string to a Uint8Array.
 */
function base64urlToBytes(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Decrypts an AES-256-CBC encrypted payload.
 * Expected format: base64url( IV[16 bytes] + ciphertext )
 * Key derived via PBKDF2(REACT_APP_ENC_KEY, salt, 10000, SHA-1).
 * Returns the decrypted string, or null on failure.
 */
export async function decryptTokenPayload(encryptedBase64url) {
  const passphrase = getEncryptionKey();
  if (!passphrase) {
    console.error('[Decrypt] REACT_APP_ENC_KEY is not set.');
    return null;
  }

  try {
    const rawBytes = base64urlToBytes(encryptedBase64url);

    console.log('[Decrypt] Passphrase:', passphrase);
    console.log('[Decrypt] Input length (chars):', encryptedBase64url.length);
    console.log('[Decrypt] Raw bytes length:', rawBytes.length);
    console.log('[Decrypt] Ciphertext length (excl IV):', rawBytes.length - IV_BYTES);
    console.log('[Decrypt] Ciphertext % 16 === 0?', (rawBytes.length - IV_BYTES) % 16 === 0);
    console.log('[Decrypt] First 32 bytes (hex):', Array.from(rawBytes.slice(0, 32)).map(b => b.toString(16).padStart(2, '0')).join(' '));

    if (rawBytes.length <= IV_BYTES) {
      console.error('[Decrypt] Encrypted data is too short.');
      return null;
    }

    // First 16 bytes = IV, rest = ciphertext
    const iv = rawBytes.slice(0, IV_BYTES);
    const ciphertext = rawBytes.slice(IV_BYTES);

    const cryptoKey = await deriveKey(passphrase);
    console.log('[Decrypt] Key derived OK. Attempting AES-CBC decrypt...');

    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-CBC', iv },
      cryptoKey,
      ciphertext
    );

    const result = new TextDecoder().decode(decryptedBuffer);
    console.log('[Decrypt] Success! Decrypted length:', result.length);
    return result;
  } catch (e) {
    console.error('[Decrypt] Failed:', e.message || e);
    return null;
  }
}

// --- End AES-256-CBC Decryption ---

/**
 * Checks whether a token value is an encrypted payload rather than a plain JWT.
 * A valid JWT always contains dots (header.payload.signature).
 * An encrypted payload is a base64url string with no dots.
 */
export function isEncryptedToken(token) {
  return !!token && typeof token === 'string' && !token.includes('.');
}

export function validateJwtStructure(token) {
  if (!token || typeof token !== 'string') return false;
  const parts = token.split('.');
  if (parts.length !== 3) return false;
  try {
    const header = JSON.parse(atob(parts[0].replace(/-/g, '+').replace(/_/g, '/')));
    if (!header || typeof header.alg !== 'string') return false;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return !!payload;
  } catch (_) {
    return false;
  }
}

export function parseTokensFromUrl(urlLike = window.location) {
  const result = {};
  // Prefer fragment (#) over query (?) for tokens
  const hash = urlLike.hash?.startsWith('#') ? urlLike.hash.slice(1) : '';
  const query = urlLike.search?.startsWith('?') ? urlLike.search.slice(1) : '';

  const parse = (s) => {
    const params = new URLSearchParams(s);
    for (const [k, v] of params.entries()) {
      result[k] = v;
    }
  };

  if (hash) parse(hash);
  if (query) parse(query);

  const hasTokens = !!(result.access_token || result.id_token || result.refresh_token);
  return { hasTokens, params: result };
}

export function sanitizeUrl() {
  try {
    const { protocol, host, pathname } = window.location;
    const clean = `${protocol}//${host}${pathname}`;
    window.history.replaceState({}, document.title, clean);
  } catch (e) {
    // best-effort only
    // eslint-disable-next-line no-console
    console.warn('Failed to sanitize URL', e);
  }
}

function decodeJwtPayload(token) {
  try {
    const [, payload] = (token || '').split('.');
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return json || null;
  } catch (_) {
    return null;
  }
}

export function getExpirationEpoch(params) {
  // Compute a conservative expiry using any available signals.
  // Prefer explicit JWT exp values; otherwise use expires_in.
  const nowSec = Math.floor(Date.now() / 1000);

  const idClaims = params.id_token ? decodeJwtPayload(params.id_token) : null;
  const atClaims = params.access_token ? decodeJwtPayload(params.access_token) : null;

  const idExp = idClaims && typeof idClaims.exp === 'number' ? idClaims.exp : 0;
  let atExp = atClaims && typeof atClaims.exp === 'number' ? atClaims.exp : 0;

  if (!atExp && params.expires_in) {
    const n = Number(params.expires_in);
    if (!Number.isNaN(n)) {
      atExp = n > 1e10 ? Math.floor(n / 1000) : nowSec + n;
    }
  }

  const candidates = [idExp, atExp].filter(Boolean);
  if (candidates.length) {
    // Use the latest expiry so session persists while at least one token remains valid
    return Math.max(...candidates);
  }
  return nowSec + 300; // default 5 minutes
}

// Session persistence helpers
const SESS_KEYS = {
  access: 'auth.access_token',
  id: 'auth.id_token',
  scope: 'auth.scope',
  exp: 'auth.expires_at',
};

// Notification-specific params stored in localStorage so they survive page reloads
const NOTIF_KEYS = {
  llId: 'notif.llId',
  trainingId: 'notif.trainingId',
};

export function persistNotificationParams({ llId, trainingId } = {}) {
  try {
    if (llId != null && llId !== '') localStorage.setItem(NOTIF_KEYS.llId, String(llId));
    if (trainingId != null && trainingId !== '') localStorage.setItem(NOTIF_KEYS.trainingId, String(trainingId));
  } catch (_) {
    // ignore storage failures
  }
}

export function loadNotificationParams() {
  try {
    const llId = localStorage.getItem(NOTIF_KEYS.llId) || '';
    const trainingId = localStorage.getItem(NOTIF_KEYS.trainingId) || '';
    return { llId, trainingId };
  } catch (_) {
    return { llId: '', trainingId: '' };
  }
}

export function clearNotificationParams() {
  try {
    localStorage.removeItem(NOTIF_KEYS.llId);
    localStorage.removeItem(NOTIF_KEYS.trainingId);
  } catch (_) {
    // ignore
  }
}

export function persistSession({ access_token, id_token, scope, expires_at }) {
  try {
    if (access_token) sessionStorage.setItem(SESS_KEYS.access, access_token);
    if (id_token) sessionStorage.setItem(SESS_KEYS.id, id_token);
    if (scope) sessionStorage.setItem(SESS_KEYS.scope, scope);
    if (expires_at) sessionStorage.setItem(SESS_KEYS.exp, String(expires_at));
  } catch (_) {
    // ignore storage failures
  }
}

export function loadSession(skewSec = 120) {
  try {
    const access_token = sessionStorage.getItem(SESS_KEYS.access);
    const id_token = sessionStorage.getItem(SESS_KEYS.id);
    const scope = sessionStorage.getItem(SESS_KEYS.scope) || '';
    const expStr = sessionStorage.getItem(SESS_KEYS.exp);
    let expires_at = expStr ? Number(expStr) : 0;
    const nowSec = Math.floor(Date.now() / 1000);

    // If no stored exp or it appears stale, try to recompute from tokens (JWT exp or heuristic)
    if (!expires_at || expires_at - skewSec <= nowSec) {
      const recomputed = getExpirationEpoch({ access_token, id_token });
      if (recomputed && recomputed - skewSec > nowSec) {
        expires_at = recomputed;
        sessionStorage.setItem(SESS_KEYS.exp, String(expires_at));
      }
    }

    if ((access_token || id_token) && expires_at && expires_at - skewSec > nowSec) {
      return { ok: true, access_token, id_token, scope, expires_at };
    }
  } catch (_) {
    // ignore
  }
  return { ok: false };
}

export function clearSession() {
  try {
    sessionStorage.removeItem(SESS_KEYS.access);
    sessionStorage.removeItem(SESS_KEYS.id);
    sessionStorage.removeItem(SESS_KEYS.scope);
    sessionStorage.removeItem(SESS_KEYS.exp);
  } catch (_) {
    // ignore
  }
}
